import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { MaterialIcon } from '@pearsontechnology/aero-components';

import enhanceWithThemes from 'hocs/enhanceWithThemes';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';
import themes from 'containers/App/themes';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { featureKeys } from 'services/FeatureFlagService';
import SideDrawer from 'components/SideDrawer';

import { PanelContainer, HelpButton, AIButton, CloseButton } from './styles';
import messages from './messages';
import TabsNavigation from './TabsNavigation';

/**
 * Displays a help button at the bottom of the screen.
 * @param {*} props
 */
function HelpMenuPanel({ getFeatureFlagValue, currentUser, ...props }) {
  const { helpContext } = props;
  let isAIEnabled = false;
  try {
    isAIEnabled = getFeatureFlagValue(featureKeys.BRONTE_AI_MCP);
  } catch {
    // Flag not yet in LD — default to hidden so HelpMenu still renders
  }
  const [panelOpen, setPanelOpen] = useState(false);
  const panelElementRef = useRef();
  const panelButtonRef = useRef();

  // Expose user initials to the standalone bronte-ai-chat widget via a global
  // variable and a custom event so the avatar shows real initials instead of "U".
  useEffect(() => {
    const me = currentUser && currentUser.get && currentUser.get('me');
    const givenName = (me && me.get && me.get('givenName')) || '';
    const familyName = (me && me.get && me.get('familyName')) || '';
    const initials = ((givenName[0] || '') + (familyName[0] || '')).toUpperCase();
    if (initials) {
      window.__BRONTE_USER_INITIALS__ = initials;
      window.dispatchEvent(new Event('bronte:user-initials'));
    }
  }, [currentUser]);

  useEffect(() => {
    const handleOuterClick = (event) => {
      if (!panelElementRef.current) return;
      const containsPanel = panelElementRef.current.contains(event.target);

      if (!containsPanel) {
        setPanelOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOuterClick);

    return function cleanup() {
      document.removeEventListener('mousedown', handleOuterClick);
    };
  }, []);

  /**
   * Listen to key presses
   */
  useEffect(() => {
    const handleKeydown = (event) => {
      if (event.code === 'Escape') {
        setPanelOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeydown);
    return function cleanup() {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  return (
    <PanelContainer ref={panelElementRef} {...props}>
      {isAIEnabled && (
        <AIButton
          onClick={() => window.bronteAIChat?.toggle()}
          aria-label={intl.formatMessage(messages.aiButton)}
        />
      )}
      {!panelOpen &&
        <HelpButton
          onClick={() => setPanelOpen(!panelOpen)}
          ref={panelButtonRef}
          aria-label={intl.formatMessage(messages.helpButton)}
        />
      }
      <SideDrawer
        isOpen={panelOpen}
        drawerWidth={'402px'}
        positionFromRight={'0'}
        positionFromTop={'0'}
        lightBackground="color.white"
        close={() => setPanelOpen(panelOpen)}
        showOverlay={false}
        zIndex='50'
      >
        <TabsNavigation helpContext={helpContext} />
        <CloseButton grey iconOnly onClick={() => setPanelOpen(!panelOpen)} >
          <MaterialIcon id="close" />
        </CloseButton>
      </SideDrawer>
    </PanelContainer>
  );
}

HelpMenuPanel.propTypes = {
  helpContext: PropTypes.string,
  currentUser: PropTypes.object,
};

const mapStateToProps = (state) => ({
  currentUser: state.get('currentUser'),
});

export default connect(mapStateToProps)(
  enhanceWithThemes(
    enhanceWithFeatureFlagSubscription(HelpMenuPanel, [featureKeys.BRONTE_AI_MCP]),
    themes
  )
);
